import { observer } from 'mobx-react-lite'
import { useKeyGenerator } from 'shared/hooks'
import {
  type IChatbotActionModel,
  ChatbotActionType,
  type IChatbotActionType,
} from 'entities/Chatbot'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type ChatbotAction } from 'pages/chatbot/store/chatbotActionStore'

import type { ActionConfig } from './type'
import { ActionCard } from './ActionCard'
import { ChatbotActionAdd } from './ActionCardAdd'
import { ActionMessage } from './ActionMessage'
import { makeActionTags } from './ActionTags'
import { ActionNote } from './ActionNote'

import styles from './styles.module.scss'

export type IChatbotActionsProps = {
  actions: ChatbotAction
  emptyPlaceholder?: string
}

const ACTIONS_CONFIG = {
  [ChatbotActionType.AddTag]: {
    icon: 'tag',
    title: 'Add tag',
    Component: makeActionTags({
      canCreate: true,
      placeholder: 'Search or create',
    }),
  },
  [ChatbotActionType.RemoveTag]: {
    icon: 'tag',
    title: 'Remove tag',
    Component: makeActionTags({
      canCreate: false,
      placeholder: 'Search',
    }),
  },
  [ChatbotActionType.ContactNote]: {
    icon: 'edit',
    title: 'Contact note',
    Component: ActionNote,
  },
  [ChatbotActionType.SendMessage]: {
    icon: 'messageTextCircle02',
    title: 'Send message',
    Component: ActionMessage,
  },
} as Record<IChatbotActionType, ActionConfig>

export const ChatbotActions = observer(({ actions, emptyPlaceholder }: IChatbotActionsProps) => {
  const getActionKey = useKeyGenerator<IChatbotActionModel>()
  const entities = useEntitiesContext()

  const handleDelete = (index: number, action: IChatbotActionModel) => {
    actions.removeAction(index)
    actions.actionErrors.removeError(action)
  }

  return (
    <div className={styles.actionCards}>
      <div className={styles.actionCardsList}>
        {!!actions.list.length ? (
          actions.list.map((action, index) => {
            const { icon, title, Component } = ACTIONS_CONFIG[action.type]

            return (
              <ActionCard
                key={getActionKey(action)}
                icon={icon}
                title={title}
                onDelete={() => handleDelete(index, action)}
              >
                <Component
                  control={actions.getActionStore(action, entities)}
                  errors={actions.actionErrors}
                  action={action}
                />
              </ActionCard>
            )
          })
        ) : (
          <>
            {!!emptyPlaceholder && (
              <div className={styles.actionCardsEmpty}>{emptyPlaceholder}</div>
            )}
          </>
        )}
      </div>
      <ChatbotActionAdd actions={actions} />
    </div>
  )
})
